import {
  AddOutlined,
  ArrowBackOutlined,
  DeleteOutline,
  EditOutlined,
  RefreshOutlined,
} from "@material-ui/icons";
import {
  Box,
  Button,
  CardHeader,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import useToast from "../../../../../../hooks/useToast";
import useConfirm from "../../../../../../hooks/useConfirm";
import { SQCQuestionService } from "../../../../../../service/SQC_form/SQCQuestionService";
import { useQuery } from "../../../../../../hooks/useQuery";
import SQCQuestionDialogForm from "./SQCQuestionDialogForm";
import { TYPE_NOTIFICATION } from "../../../../shares/NotificationBull";

const SQCFormQuestionList = ({
  selection = false,
  id_type,
  handleSelectedCV,
}) => {
  const [questions, setQuestions] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const navigate = useNavigate();
  const [filtre, setFiltre] = useState({});
  const { Toast, showToast } = useToast();
  const { DialogComponent, openConfirmDialog } = useConfirm();
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [idSelectedQuestion, setIdSelectedQuestion] = useState(null);
  const url = useQuery();
  const id_form = url.get("form_id");

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleOpenDialogForm = (idForm) => {
    setOpenFormDialog(true);
    setIdSelectedQuestion(idForm);
  };

  const handleCloseDialogForm = () => {
    setOpenFormDialog(false);
    handleFetchQuestionsList();
  };

  const handleDeleteRow = (id) => {
    openConfirmDialog(
      "Confirmation",
      "Voullez vous supprimer cette ligne?",
      () => {
        handleConfirmDeleteRow(id);
      }
    );
  };

  const handleConfirmDeleteRow = (id) => {
    SQCQuestionService.delete(id).then((res) => {
      if (res && res.success) {
        showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
        handleFetchQuestionsList();
      } else {
        showToast(TYPE_NOTIFICATION.ERROR, res.message);
      }
    });
  };

  const handleFetchQuestionsList = () => {
    setDataLoading(true);
    SQCQuestionService.list({ ...filtre, id_sqc_form: id_form })
      .then((res) => {
        if (res && res.data) {
          setQuestions(res.data);
          setFilteredRows(res.data);
        } else {
          setQuestions([]);
          setFilteredRows([]);
        }
      })
      .finally(() => {
        setDataLoading(false);
      });
  };

  const handleFilter = (filtre) => {
    if (!filtre || String(filtre).trim() === "") {
      setFilteredRows(questions);
      return;
    }
    const mots = filtre
      .toLowerCase()
      .split(" ")
      .filter((mot) => mot.trim() !== "");
    setFilteredRows(
      questions.filter((item) =>
        mots.every((mot) =>
          Object.values(item).some(
            (val) => val && val.toString().toLowerCase().includes(mot)
          )
        )
      )
    );
  };

  const table_colonne = [
    {
      field: "question",
      headerName: "Question",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "qs_requis",
      headerName: "Reponse obligatoire",
      minWidth: 150,
      renderCell: (params) => {
        return params.row.qs_requis === "1" ? "Oui" : "Non";
      },
    },
    {
      field: "pj_requis",
      headerName: "Piece jointe obligatoire",
      minWidth: 150,
      renderCell: (params) => {
        return params.row.pj_requis === "1"
          ? "Oui"
          : params.row.pj_requis === "2"
            ? "Masqué"
            : "Non";
      },
    },
    {
      field: "allow_comment",
      headerName: "Commentaire autorisé",
      minWidth: 150,
      renderCell: (params) => {
        return params.row.allow_comment === "1" ? "Oui" : "Non";
      },
    },
    {
      field: "comment_requis",
      headerName: "Commentaire obligatoire",
      minWidth: 150,
      renderCell: (params) => {
        return params.row.comment_requis === "1" ? "Oui" : params.row.comment_requis === "2" ? "Si reponse 'oui'" : "Non";
      },
    },
    {
      field: "created_at",
      headerName: "Date de création",
      minWidth: 120,
    },
    {
      field: "updated_at",
      headerName: "Date de modification",
      minWidth: 120,
    },
    {
      field: "action",
      headerName: "Actions",
      minWidth: 180,
      renderCell: (params) => {
        return (
          <Grid>
            <IconButton
              title="Modifier"
              color="primary"
              onClick={(e) => handleOpenDialogForm(params.row.id_sqc_question)}
            >
              <EditOutlined />
            </IconButton>
            <IconButton
              title="Supprimer"
              color="warning"
              onClick={(e) => handleDeleteRow(params.row.id_sqc_question)}
            >
              <DeleteOutline />
            </IconButton>
          </Grid>
        );
      },
    },
  ];

  useEffect(() => {
    handleFetchQuestionsList();
  }, []);

  return (
    <>
      <SQCQuestionDialogForm
        open={openFormDialog}
        idForm={id_form}
        idQuestion={idSelectedQuestion}
        onFormClose={handleCloseDialogForm}
      />
      <DialogComponent />
      <Toast />
      <Grid width={"100%"}>
        <Paper elevation={0}>
          <Grid>
            <CardHeader
              title="Questionnaires SQC"
              titleTypographyProps={{ variant: "h5" }}
              avatar={
                <Button onClick={handleGoBack}>
                  <ArrowBackOutlined />
                </Button>
              }
              action={
                <Grid container spacing={2} justifyContent="space-around">
                  <Grid item>
                    <TextField
                      id="recherche"
                      fullWidth
                      size="small"
                      onChange={(e) => handleFilter(e.target.value)}
                      placeholder="Recherche"
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <IconButton
                      title="Actualiser"
                      color="primary"
                      onClick={handleFetchQuestionsList}
                    >
                      <RefreshOutlined />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      onClick={(e) => handleOpenDialogForm(null)}
                      color="primary"
                      title="Creer une questionnaire"
                    >
                      <AddOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              }
            />
            <Grid container>
              <Box display={dataLoading ? "" : "none"} sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
              <DataGrid
                sx={{
                  width: {
                    xs: "75.5vw",
                    sm: "90vw",
                    md: "93.5vw",
                    lg: "79.5vw",
                    height: "60vh",
                  },
                }}
                columns={table_colonne}
                rows={filteredRows}
                // rows={questions}
                getRowId={(row) => row.id_sqc_question}
                localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
              />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </>
  );
};

export default SQCFormQuestionList;
